import './types';
import '@fontsource/source-sans-pro/300.css';
import '@fontsource/source-sans-pro/400.css';
import '@fontsource/source-sans-pro/600.css';
import '@fontsource/source-sans-pro/700.css';
import { CheckCircleTwoTone, ErrorTwoTone, InfoTwoTone } from '@mui/icons-material';
import { alpha, createTheme } from '@mui/material';
import {
  amber,
  blue,
  common,
  cyan,
  deepPurple,
  green,
  grey,
  indigo,
  lightBlue,
  lightGreen,
  lime,
  orange,
  pink,
  purple,
  red,
  teal,
  yellow
} from '@mui/material/colors';

import {
  EMM_CHIP_DEFAULTS,
  EMM_DONUT_DEFAULTS,
  EMM_GRID_DEFAULTS,
  EMM_SEGMENTED_BAR_DEFAULTS,
  EMM_TABS_DEFAULTS,
  EMM_TINT_DEFAULTS,
  EMM_WIDGET_DEFAULTS
} from './defaults';

import type { PaletteMode, Theme } from '@mui/material';
import type { Shadows } from '@mui/material/styles';

// Severity scale — all MUI [400] shades for a consistent visual weight.
// Highest  red[400]    #EF5350  white text
// High     orange[400] #FFA726  dark text
// Moderate amber[400]  #FFCA28  dark text
// Low      green[400]  #66BB6A  white text
// Minimal  teal[400]   #26A69A  dark text
const SEVERITY = {
  highest: { main: red[400], contrastText: common.white },
  high: { main: orange[400], contrastText: 'rgba(0,0,0,0.87)' },
  moderate: { main: amber[400], contrastText: 'rgba(0,0,0,0.87)' },
  low: { main: green[400], contrastText: common.white },
  minimal: { main: teal[400], contrastText: common.white }
};

// CTEM stage palette — derived from the legacy brand infinity-loop graphic, mapped to
// the closest MUI [900] shade so the values live in the theme instead of as raw hex.
// Original mapping:
//   Scoping        #1d3147  ≈  indigo[900]  #263238
//   Discovery      #325d6e  ≈  cyan[900]      #006064
//   Validation     #735528  ≈  yellow[900]    #FF6F00
//   Prioritization #6b3443  ≈  pink[900]      #880E4F
//   Mobilization   #442167  ≈  purple[900]    #4A148C
const STAGES = {
  scoping: { main: alpha(indigo[900], 0.6), contrastText: common.white },
  discovery: { main: alpha(cyan[900], 0.6), contrastText: common.white },
  validation: { main: alpha(yellow[900], 0.6), contrastText: common.white },
  prioritization: { main: alpha(pink[900], 0.6), contrastText: common.white },
  mobilization: { main: alpha(purple[900], 0.6), contrastText: common.white }
};

// Data source palette — stable per-vendor tints used by venn diagrams, source dots,
// row swatches, and any chart that needs to distinguish one ingestion source from
// another. MUI [300] shade so they read as muted accents against EMM dark surfaces
// and don't fight severity colors. Mirrors the legacy `getColorBySource` mapping.
const DATA_SOURCES = {
  aiqDiscovery: deepPurple[300],
  crowdstrike: red[300],
  sentinelone: lime[300],
  msde: blue[300],
  qualys: purple[300],
  rapid7: lightBlue[300],
  tenable: lightGreen[300],
  unknown: grey[400]
};

// OS palette — distribution charts and OS dots. MUI [400] shade for slightly more
// presence than `dataSource` since OS slices tend to be larger than per-source
// slivers. Mirrors the legacy `getColorByOperatingSystem` util.
const OS = {
  windows: lightBlue[400],
  linux: amber[400],
  mac: lightGreen[400],
  other: grey[400]
};

// EMM-brand dark surfaces. Five-layer stack, each step ≈ +7/8/9 per RGB channel
// so deltas read as real boundaries without going muddy. Order from deepest to
// brightest: default (canvas) → surface (recessed/nested) → paper (primary
// container) → raised (opt-in emphasis) → overlay (floating: dialogs, menus,
// tooltips, drawers). Tinted blue-purple; no clean MUI shade equivalent.
const EMM_DARK = {
  bgDefault: '#141720',
  bgSurface: '#181B2A',
  bgPaper: '#1C2235',
  bgRaised: '#232A3E',
  bgOverlay: '#2A3247'
};

// EMM-brand light surfaces — neutral off-whites with a very subtle cool hint.
// `raised` and `overlay` both pin to pure white: in light mode there's no
// headroom above `#FFFFFF`, so elevation above `paper` is communicated via
// shadow + border (rendered by Menu/Dialog/Drawer/Tooltip natively) rather
// than color. Reserves the cool off-whites for in-flow content layering.
//
// `palette.divider` is intentionally NOT a fixed hex on either mode — it's
// `alpha(common.white|black, 0.1)` so rules and outlines read as a faint tint
// of the dominant foreground rather than a fixed slate gray. Matches the
// chrome on EmmMetricStrip / EmmClassificationTag.
const EMM_LIGHT = {
  bgDefault: '#ECEEF2',
  bgSurface: '#F5F6F8'
};

// Border radius spec
// Buttons + inputs: 6px
// Widgets/containers: 12px
// Chips: controlled by emmChip token (pill)

// Soft shadow scale — replaces MUI's 25-entry `shadows` tuple. Two-layer formula
// (tight key + diffuse ambient) with low max opacity (~0.12 at elevation 24) so
// elevation reads as a subtle halo rather than a hard drop. By overriding the
// whole tuple, every consumer that reads `theme.shadows[N]` (buttons, menus,
// dialogs, popovers, drawers, tooltips) shares the same softness profile.
// MUI's defaults peak at ~0.2 alpha even at low elevations — this scale is
// roughly half that, with a more diffuse geometry.
const softShadow = (n: number): string => {
  if (n === 0) return 'none';
  const y1 = Math.max(1, Math.round(n * 0.5));
  const blur1 = Math.max(1, n);
  const a1 = Math.min(0.12, 0.04 + n * 0.003);
  const y2 = Math.max(1, n);
  const blur2 = Math.max(2, n * 2);
  const a2 = Math.min(0.07, 0.03 + n * 0.002);
  return `0 ${y1}px ${blur1}px rgba(0,0,0,${a1.toFixed(3)}), 0 ${y2}px ${blur2}px rgba(0,0,0,${a2.toFixed(3)})`;
};

const EMM_SHADOWS = Array.from({ length: 25 }, (_, i) => softShadow(i)) as Shadows;

export const createUnifiedEmmTheme = (mode: PaletteMode): Theme =>
  createTheme({
    shadows: EMM_SHADOWS,
    palette: {
      mode,
      severity: SEVERITY,
      stage: STAGES,
      dataSource: DATA_SOURCES,
      os: OS,
      ...(mode === 'dark'
        ? {
            primary: {
              main: deepPurple[500],
              dark: deepPurple[700],
              light: deepPurple[300],
              contrastText: common.white
            },
            secondary: {
              main: cyan[500],
              dark: cyan[700],
              light: cyan[300],
              contrastText: common.white
            },
            background: {
              default: EMM_DARK.bgDefault,
              surface: EMM_DARK.bgSurface,
              paper: EMM_DARK.bgPaper,
              raised: EMM_DARK.bgRaised,
              overlay: EMM_DARK.bgOverlay
            },
            divider: alpha(common.white, 0.05),
            text: {
              primary: common.white,
              secondary: 'rgba(255,255,255,0.55)',
              disabled: 'rgba(255,255,255,0.35)'
            },
            action: {
              active: 'rgba(255,255,255,0.56)',
              hover: alpha(deepPurple[400], 0.08),
              selected: alpha(deepPurple[400], 0.16),
              disabled: 'rgba(255,255,255,0.38)',
              disabledBackground: 'rgba(255,255,255,0.12)'
            }
          }
        : {
            primary: {
              main: deepPurple[500],
              dark: deepPurple[700],
              light: deepPurple[300],
              contrastText: common.white
            },
            secondary: {
              main: cyan[700],
              dark: cyan[900],
              light: cyan[400],
              contrastText: common.white
            },
            background: {
              default: EMM_LIGHT.bgDefault,
              surface: EMM_LIGHT.bgSurface,
              paper: common.white,
              raised: common.white,
              overlay: common.white
            },
            divider: alpha(common.black, 0.1),
            // Neutral dark for text — softer than pure #141720, lower visual fatigue
            // while still well above the AA threshold (~12:1 on the new bg).
            text: {
              primary: 'rgba(0,0,0,0.87)',
              secondary: 'rgba(0,0,0,0.6)',
              disabled: 'rgba(0,0,0,0.38)'
            },
            action: {
              active: 'rgba(0,0,0,0.54)',
              hover: alpha(deepPurple[500], 0.06),
              selected: alpha(deepPurple[500], 0.12),
              disabled: 'rgba(0,0,0,0.38)',
              disabledBackground: 'rgba(0,0,0,0.12)'
            }
          }),
      error: { main: mode === 'dark' ? red[500] : red[700], contrastText: common.white },
      warning: { main: mode === 'dark' ? amber[500] : amber[800], contrastText: 'rgba(0,0,0,0.87)' },
      info: { main: mode === 'dark' ? blue[500] : blue[700], contrastText: common.white },
      success: { main: mode === 'dark' ? green[500] : green[700], contrastText: common.white }
    },
    typography: {
      fontFamily: '"Source Sans Pro", "Helvetica", "Arial", sans-serif',
      // Source Sans Pro has no 500 weight — 600 is the semibold step
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 600,
      fontWeightBold: 700,
      // Scale anchored at body1 = 14px / 400. Browser default root = 16px.
      // Wider range across headings to give clearer hierarchy.
      h1: { fontSize: '3rem', fontWeight: 300, lineHeight: 1.15, letterSpacing: '-0.02em' }, // 48px
      h2: { fontSize: '2.25rem', fontWeight: 300, lineHeight: 1.2, letterSpacing: '-0.015em' }, // 36px
      h3: { fontSize: '1.75rem', fontWeight: 400, lineHeight: 1.25, letterSpacing: '-0.01em' }, // 28px
      h4: { fontSize: '1.375rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.005em' }, // 22px
      h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35, letterSpacing: 0 }, // 18px
      h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4, letterSpacing: 0 }, // 16px
      subtitle1: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: 0 }, // 15px
      subtitle2: { fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: 0 }, // 13px
      body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5, letterSpacing: 0 }, // 14px ← anchor
      body2: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.5, letterSpacing: 0 }, // 13px
      button: {
        fontSize: '0.8125rem',
        fontWeight: 600,
        lineHeight: 1.5,
        letterSpacing: '0.01em',
        textTransform: 'none' as const
      }, // 13px
      caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4, letterSpacing: '0.02em' }, // 12px
      overline: {
        fontSize: '0.6875rem',
        fontWeight: 400,
        lineHeight: 1.4,
        letterSpacing: '0.08em',
        textTransform: 'uppercase' as const
      } // 11px
    },
    // Component-token blocks — values live in `./defaults.ts` as the single source of truth,
    // shared with each component's out-of-theme fallback so the two can never drift.
    emmChip: EMM_CHIP_DEFAULTS,
    emmWidget: EMM_WIDGET_DEFAULTS,
    emmGrid: EMM_GRID_DEFAULTS,
    emmTabs: EMM_TABS_DEFAULTS,
    emmSegmentedBar: EMM_SEGMENTED_BAR_DEFAULTS,
    emmDonut: EMM_DONUT_DEFAULTS,
    emmTint: EMM_TINT_DEFAULTS,
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            padding: '8px 16px',
            textTransform: 'none',
            fontWeight: 600
          },
          sizeSmall: {
            padding: '4px 10px'
          }
        }
      },
      // Match the EmmTabs "buttons + container" look: tinted container with rounded,
      // text-style buttons inside. No selected state for a plain ButtonGroup.
      MuiButtonGroup: {
        defaultProps: {
          disableElevation: true,
          disableRipple: true
        },
        styleOverrides: {
          root: ({ theme }) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            padding: theme.spacing(0.5),
            borderRadius: 10,
            backgroundColor: alpha(theme.palette.text.primary, 0.06),
            boxShadow: 'none',
            '& .MuiButtonGroup-grouped': {
              minHeight: 32,
              minWidth: 'auto',
              padding: theme.spacing(0.5, 1.5),
              borderRadius: 6,
              textTransform: 'none',
              fontSize: theme.typography.body2.fontSize,
              fontWeight: 400,
              color: theme.palette.text.primary,
              backgroundColor: 'transparent',
              border: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: alpha(theme.palette.text.primary, 0.06),
                border: 'none',
                boxShadow: 'none'
              },
              '&:not(:last-of-type)': {
                borderRight: 'none',
                marginRight: 0
              },
              '&:not(:first-of-type)': {
                borderLeft: 'none',
                marginLeft: 0
              }
            }
          })
        }
      },
      // Same chrome as ButtonGroup, plus an active state via .Mui-selected.
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: ({ theme }) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            padding: theme.spacing(0.5),
            borderRadius: 10,
            backgroundColor: alpha(theme.palette.text.primary, 0.06),
            '& .MuiToggleButtonGroup-grouped': {
              minHeight: 32,
              minWidth: 'auto',
              padding: theme.spacing(0.5, 1.5),
              borderRadius: 6,
              textTransform: 'none',
              fontSize: theme.typography.body2.fontSize,
              fontWeight: 400,
              color: theme.palette.text.primary,
              backgroundColor: 'transparent',
              border: 'none',
              '&:hover': {
                backgroundColor: alpha(theme.palette.text.primary, 0.06)
              },
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  filter: 'brightness(0.9)'
                }
              },
              '&:not(:last-of-type), &:not(:first-of-type)': {
                borderRadius: 6,
                marginLeft: 0,
                border: 'none'
              }
            }
          })
        }
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 12,
            backgroundImage: 'none',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none'
          })
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 12
          },
          outlined: ({ theme }) => ({
            borderColor: theme.palette.divider
          })
        }
      },
      MuiAlert: {
        defaultProps: {
          iconMapping: {
            error: <ErrorTwoTone fontSize="inherit" />,
            warning: <ErrorTwoTone fontSize="inherit" />,
            info: <InfoTwoTone fontSize="inherit" />,
            success: <CheckCircleTwoTone fontSize="inherit" />
          }
        },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 6,
            padding: '4px 12px',
            // Dark mode: 25% black wash. Light mode: pure white card; the severity
            // border + icon carry the meaning instead of a muddy dark overlay.
            backgroundColor:
              theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.25) : theme.palette.common.white,
            color: theme.palette.text.primary,
            border: '1px solid'
          }),
          standardError: ({ theme }) => ({
            borderColor: alpha(theme.palette.error.main, 0.3),
            '& .MuiAlert-icon': { color: theme.palette.error.main }
          }),
          standardWarning: ({ theme }) => ({
            borderColor: alpha(theme.palette.warning.main, 0.3),
            '& .MuiAlert-icon': { color: theme.palette.warning.main }
          }),
          standardInfo: ({ theme }) => ({
            borderColor: alpha(theme.palette.info.main, 0.3),
            '& .MuiAlert-icon': { color: theme.palette.info.main }
          }),
          standardSuccess: ({ theme }) => ({
            borderColor: alpha(theme.palette.success.main, 0.3),
            '& .MuiAlert-icon': { color: theme.palette.success.main }
          })
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 6
          },
          // `text.disabled` (rgba alpha ~0.35 dark / 0.38 light) reads as a legible border against
          // both EMM surfaces. `divider` was too low-contrast — the input perimeter disappeared.
          notchedOutline: ({ theme }) => ({
            borderColor: theme.palette.text.disabled
          })
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundImage: 'none',
            borderRadius: 0,
            backgroundColor: theme.palette.background.overlay
          })
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: 12,
            backgroundColor: theme.palette.background.overlay
          })
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: 12,
            backgroundColor: theme.palette.background.overlay
          })
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            backgroundColor: theme.palette.background.overlay,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(0.75, 1.25),
            fontSize: 12,
            fontWeight: 400,
            boxShadow: theme.shadows[4]
          }),
          arrow: ({ theme }) => ({
            color: theme.palette.background.overlay,
            '&::before': {
              border: 'none'
            }
          })
        }
      }
    }
  });
